import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { ExternalLink, Phone, MessageSquare } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Frequency capping: how many times has this ad been shown this session?
function sessionImpressions(adId) {
  try {
    const raw = sessionStorage.getItem("ns_ad_freq") || "{}";
    return JSON.parse(raw)[adId] || 0;
  } catch { return 0; }
}
function bumpSessionImpression(adId) {
  try {
    const raw = sessionStorage.getItem("ns_ad_freq") || "{}";
    const obj = JSON.parse(raw);
    obj[adId] = (obj[adId] || 0) + 1;
    sessionStorage.setItem("ns_ad_freq", JSON.stringify(obj));
    // Track recent-shown ids per placement so we can send `exclude` next request
    const raw2 = sessionStorage.getItem("ns_ad_shown") || "[]";
    const arr = JSON.parse(raw2);
    if (!arr.includes(adId)) arr.push(adId);
    while (arr.length > 20) arr.shift();
    sessionStorage.setItem("ns_ad_shown", JSON.stringify(arr));
  } catch {}
}
function getShownIds() {
  try { return JSON.parse(sessionStorage.getItem("ns_ad_shown") || "[]"); } catch { return []; }
}

/**
 * Full-width sponsored banner slot. Renders the highest-priority active ad
 * for the given placement + optional category. Auto-tracks impression + click.
 * Respects `display_frequency` for per-session frequency capping.
 */
export default function SponsoredBanner({ placement, category, className = "" }) {
  const [ad, setAd] = useState(null);
  const impressed = useRef(false);

  useEffect(() => {
    const params = { placement };
    if (category) params.category = category;
    // Anti-repeat: pass shown ad IDs so backend rotates to a fresh one
    const excluded = getShownIds();
    if (excluded.length) params.exclude = excluded.join(",");
    api.get("/sponsored-ads", { params }).then((r) => {
      const first = (r.data.ads || [])[0];
      if (!first) { setAd(null); return; }
      // Frequency cap: skip if we've hit display_frequency for this session
      const cap = Number(first.display_frequency || 0);
      if (cap > 0 && sessionImpressions(first.id) >= cap) {
        setAd(null);
        return;
      }
      setAd(first);
      if (!impressed.current) {
        impressed.current = true;
        bumpSessionImpression(first.id);
        api.post(`/sponsored-ads/${first.id}/impression`).catch(() => {});
      }
    }).catch(() => setAd(null));
  }, [placement, category]);

  if (!ad) return null;

  const bannerUrl = ad.banner_path ? `${BACKEND}/api/sponsored-ads/media/${ad.banner_path}` : "";
  const logoUrl = ad.logo_path ? `${BACKEND}/api/sponsored-ads/media/${ad.logo_path}` : "";
  const waLink = ad.whatsapp ? `https://wa.me/${String(ad.whatsapp).replace(/\D/g, "")}` : null;

  const openWebsite = () => {
    api.post(`/sponsored-ads/${ad.id}/click`).catch(() => {});
    window.open(ad.website_url, "_blank", "noopener,noreferrer");
  };

  return (
    <section data-testid={`sponsored-banner-${placement}`} className={`space-y-1 ${className}`}>
      <p className="text-[10px] uppercase tracking-[0.15em] text-[#F5C462] px-1">Sponsored</p>
      <div
        role="link"
        tabIndex={0}
        onClick={openWebsite}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openWebsite(); } }}
        data-testid={`sponsored-ad-${ad.id}`}
        className="rounded-2xl overflow-hidden border border-[#F5C462]/30 bg-gradient-to-br from-[#180D0D] to-[#0A0605] hover:border-[#F5C462]/60 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#F5C462]/50"
      >
        {bannerUrl && (
          <div className="h-28 w-full bg-black overflow-hidden">
            <img src={bannerUrl} alt={ad.business_name} loading="lazy" className="w-full h-full object-cover"/>
          </div>
        )}
        <div className="p-3 flex items-center gap-3">
          {logoUrl && <img src={logoUrl} alt="" loading="lazy" className="w-10 h-10 rounded-lg object-cover border border-zinc-800 shrink-0"/>}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate flex items-center gap-1">
              {ad.business_name}
              <ExternalLink size={11} className="text-zinc-400"/>
            </p>
            <p className="text-[11px] text-zinc-300 truncate line-clamp-2 leading-snug">{ad.description}</p>
          </div>
          <div className="flex flex-col gap-1 shrink-0">
            {ad.phone && (
              <a data-testid={`sponsored-call-${ad.id}`} href={`tel:${ad.phone}`} onClick={(e) => e.stopPropagation()} className="h-7 px-3 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white text-[11px] font-semibold inline-flex items-center gap-1"><Phone size={10}/>Call</a>
            )}
            {waLink && (
              <a data-testid={`sponsored-wa-${ad.id}`} href={waLink} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="h-7 px-3 rounded-full bg-[#25D366] hover:bg-[#20BA5A] text-black text-[11px] font-semibold inline-flex items-center gap-1"><MessageSquare size={10}/>WA</a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
